import { useState, useEffect } from "react";
import { useFarm } from "../context/FarmContext";
import { CURRENCIES, LIVESTOCK, livestockById } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { SlidersHorizontal, Check, Beef, Egg, Milk, PiggyBank, Cloud } from "lucide-react";
import { toast } from "sonner";

const ICONS = { Beef, Egg, Milk, PiggyBank, Cloud };

export default function Settings() {
  const { settings, updateSettings } = useFarm();
  const [name, setName] = useState(settings.farm_name);
  const [livestock, setLivestock] = useState(settings.active_livestock);
  const [currency, setCurrency] = useState(settings.currency_code);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(settings.farm_name);
    setLivestock(settings.active_livestock);
    setCurrency(settings.currency_code);
  }, [settings]);

  const save = async () => {
    if (!name.trim()) return toast.error("Farm name is required");
    setSaving(true);
    const c = CURRENCIES.find((x) => x.code === currency);
    await updateSettings({
      farm_name: name.trim(),
      active_livestock: livestock,
      currency_code: c.code,
      currency_symbol: c.symbol,
    });
    setSaving(false);
    toast.success("Farm profile saved");
  };

  const dirty =
    name !== settings.farm_name ||
    livestock !== settings.active_livestock ||
    currency !== settings.currency_code;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Farm Profile</h1>
        <p className="text-sm text-muted-foreground mt-1">Rename your farm and set default livestock and currency.</p>
      </div>

      <Card className="p-6 sm:p-8 space-y-7">
        <div className="flex items-center gap-2 text-accent">
          <SlidersHorizontal className="h-5 w-5" />
          <span className="font-display font-bold text-foreground">General</span>
        </div>

        <div>
          <Label htmlFor="farm-name">Farm Name</Label>
          <Input
            id="farm-name"
            className="mt-1.5"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Green Valley Livestock Farm"
            data-testid="farm-name-input"
          />
        </div>

        <div>
          <Label>Default Livestock</Label>
          <p className="text-xs text-muted-foreground mb-2 mt-0.5">Sets the focus across the dashboard and activities.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {LIVESTOCK.map((l) => {
              const I = ICONS[l.icon] || Beef;
              const activeSel = livestock === l.id;
              return (
                <button
                  key={l.id}
                  onClick={() => setLivestock(l.id)}
                  data-testid={`profile-livestock-${l.id}`}
                  className={`flex items-center gap-2.5 rounded-xl border p-3 text-left transition-colors ${
                    activeSel ? "border-primary bg-primary/8" : "border-border hover:bg-secondary"
                  }`}
                >
                  <span className={`grid h-9 w-9 place-items-center rounded-lg shrink-0 ${activeSel ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                    <I className="h-4.5 w-4.5" />
                  </span>
                  <span className="leading-tight">
                    <span className="block text-sm font-semibold">{l.name}</span>
                    <span className="block text-[11px] text-muted-foreground">{l.subtitle}</span>
                  </span>
                  {activeSel && <Check className="ml-auto h-4 w-4 text-primary" />}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label>Default Currency</Label>
          <p className="text-xs text-muted-foreground mb-2 mt-0.5">Applied to all financial figures across the app.</p>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="w-full sm:w-72" data-testid="profile-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[320px]">
              {CURRENCIES.map((c) => (
                <SelectItem key={c.code} value={c.code} data-testid={`profile-currency-${c.code}`}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border pt-6">
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button onClick={save} disabled={!dirty || saving} className="rounded-full gap-1.5" data-testid="save-profile-button">
            <Check className="h-4 w-4" /> {saving ? "Saving…" : "Save Profile"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
